// Dynamic Data (ADR-002): collection / field / record. Giá trị lưu trong `values` theo id field (không theo tên). Mã hoá giá trị:
//   text → chuỗi · number → chuỗi thập phân · money → chuỗi số nguyên minor unit (K5) · date → YYYY-MM-DD · bool · select → id
//   option · relation → id record (bản số one). Không có `file` ở v1 (Q4).
import { IsoDateTime, list, success, Uuid } from "@secretaa/http-contract";
import { z } from "zod";

export const FieldType = z.enum(["text", "number", "money", "date", "bool", "select", "relation"]);
export type FieldType = z.infer<typeof FieldType>;
export const FieldStatus = z.enum(["active", "retired", "purged"]);
export const I18nLabel = z.object({ vi: z.string().min(1).max(120), en: z.string().min(1).max(120) }).strict();

export const SelectOption = z.object({ id: Uuid, key: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/), label: I18nLabel, status: z.enum(["active", "retired"]) }).strict();
export const FieldConfig = z.union([
  z.object({}).strict(),
  z.object({ currency: z.string().regex(/^[A-Z]{3}$/), minorUnitScale: z.number().int().min(0).max(4) }).strict(),
  z.object({ options: z.array(SelectOption).min(1).max(200) }).strict(),
  z.object({ targetCollectionId: Uuid, cardinality: z.literal("one") }).strict(),
]);
export type FieldConfig = z.infer<typeof FieldConfig>;

export const Field = z
  .object({ id: Uuid, collectionId: Uuid, key: z.string(), label: I18nLabel, type: FieldType, required: z.boolean(), indexed: z.boolean(), status: FieldStatus, config: FieldConfig })
  .strict();
export const Collection = z
  .object({ id: Uuid, projectId: Uuid, key: z.string(), label: I18nLabel, status: z.enum(["active", "retired"]), manifestVersion: z.number().int(), fields: z.array(Field) })
  .strict();
export type Collection = z.infer<typeof Collection>;
export const CollectionsResponse = success(z.array(Collection));

export const RecordValues = z.record(Uuid, z.union([z.string().max(10_000), z.boolean(), z.null()]));
export const DataRecord = z
  .object({
    id: Uuid,
    projectId: Uuid,
    collectionId: Uuid,
    scopeId: Uuid.nullable(),
    values: RecordValues,
    version: z.number().int(),
    createdBy: Uuid,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    deletedAt: IsoDateTime.nullable(),
  })
  .strict();
export type DataRecord = z.infer<typeof DataRecord>;
export const CreateRecordBody = z.object({ scopeId: Uuid.nullable().default(null), values: RecordValues }).strict();
export const UpdateRecordBody = z
  .object({ scopeId: Uuid.nullable().optional(), values: RecordValues.optional() })
  .strict()
  .refine((v) => v.scopeId !== undefined || v.values !== undefined, "cần ít nhất một trường");
export const RecordSort = z.object({ field: z.union([Uuid, z.literal("createdAt")]), dir: z.enum(["asc", "desc"]) }).strict();
/** Lọc bằng SecretAAExpression (validator ở server theo field của collection); chỉ field `indexed` mới lọc / sắp xếp được. */
export const QueryRecordsBody = z
  .object({
    filter: z.unknown().optional(),
    sort: RecordSort.default({ field: "createdAt", dir: "desc" }),
    scopeId: Uuid.optional(),
    deleted: z.boolean().default(false),
    cursor: z.string().max(1024).optional(),
    limit: z.number().int().min(1).max(200).default(50),
  })
  .strict();
export const RecordResponse = success(DataRecord);
export const RecordsResponse = list(DataRecord);
